<script setup lang="ts">
import {
    CAPACITY_METHOD_ENERGIE_AVEC_COEFF,
    CAPACITY_METHOD_ENERGIE_SANS_COEFF,
    DEFAULT_PRICER_CAPACITY_VALUE as DEFAULT_PRICER_CAPACITY_VALUE,
    OptionTarifaire,
} from "@modules/courtage/constants"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import {
    CoefficientCapacitePlage,
    ComparatorInputOffreActuelle,
    ComparatorInputOffreComparee,
    ComparatorInputPricerOffer,
    FormOfferModel,
    PeriodItem,
    PrixHTTParKwgElecsoutiragePlage,
    Source,
} from "@types"
import moment from "moment"
import { storeToRefs } from "pinia"
import { onBeforeMount, ref, useTemplateRef, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import ComparatorOfferItem from "@modules/courtage/components/comparator/item/ComparatorOfferItem.vue"

const { t, te } = useI18n({
    useScope: "global",
    messages: {
        fr: {
            title: "Comparateur d'offres",
            parameters: "Paramètres de la comparaison",
            add_offer: "Nouvelle offre alternative",
            launch_comparison: "Valider et comparer",
            error_compare_offers:
                "Veuillez vérifier les informations saisies dans les formulaires.",
            code_error: {
                "trous-donnees":
                    "Les données sont insuffisantes car il y a des trous dans les données. Veuillez sélectionner une période sans trous pour pricer un contrat.",
                general:
                    "Une erreur est survenue. Veuillez réessayer plus tard.",
                no_period_selected:
                    "Veuillez sélectionner une période personnalisée.",
                "aucune-donnee":
                    "Il n'y a pas de données pour ce compteur sur la période de consommation de référence sélectionnée. Veuillez sélectionner une autre période.",
            },
            edit: "Modifier",
            cancel_edit: "Annuler et revenir aux résultats",
            validate_modifications: "Valider les modifications et comparer",
            new_comparison: "Nouvelle comparaison",
        },
    },
})

const buildingStore = useBuildingStore()

const comparatorStore = useComparatorStore()

const { building } = storeToRefs(buildingStore)
const {
    isFetchingCompareOffers,
    getSourceById,
    currentAnalysis,
    offreActuelle,
    offresComparees,
    hasErrorCompareOffers,
    errorCompareOffers,
    consommationByPlageFournisseur,
    useEnergieparposteElec,
} = storeToRefs(comparatorStore)

const route = useRoute()

onBeforeMount(async () => {
    await fetchInitialData()
})

const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async () => {
        // Récupère les sources du bâtiment avec le comparateur
        await comparatorStore.fetchSourceByIds(
            building.value?.sources
                .filter(
                    (source: Source) =>
                        (source?.idtype || source?.type) === "pdl"
                )
                .map((source: Source) => source._id_source) || []
        )

        // Récupère les fournisseurs
        await comparatorStore.fetchSuppliers()
    })

/**
 * Reset du store + re-fetch des sources/suppliers au changement de bâtiment
 * via navigation SPA (DEV-380, parent DEV-378). Sans ce watcher, `sourceSelected`
 * et la liste des sources héritent du bâtiment précédent → la requête pricer
 * envoie un `_id_source` qui n'appartient pas au bâtiment courant.
 */
watch(
    () => route.params._id_building,
    (newBid, prevBid) => {
        if (!prevBid || !newBid || prevBid === newBid) return
        comparatorStore.$reset()
        void fetchInitialData()
    }
)

const modelOffreActuelle = ref<FormOfferModel>({
    supplier: "",
    offer: OptionTarifaire.Base,
    name: "",
    periods: [],
})

const modelOffreComparees = ref<FormOfferModel[]>([
    {
        supplier: "",
        offer: OptionTarifaire.Base,
        name: "",
        periods: [],
        indexTransition: 0,
    },
])

const indexTransition = ref(0)

/**
 * Quand l'utilisateur clique sur ajouter une offre alternative
 */
function onClickAddOffer(): void {
    indexTransition.value++
    modelOffreComparees.value.push({
        supplier: "",
        offer: OptionTarifaire.Base,
        name: moment().format("YYYY"),
        periods: [],
        indexTransition: indexTransition.value,
    })
}

/**
 * Quand l'utilisateur clique sur supprimer une offre alternative
 * @param index
 */
function onClickDeleteOffer(index: number): void {
    modelOffreComparees.value.splice(index, 1)
}

type ComparatorOfferItemInstance = InstanceType<typeof ComparatorOfferItem>

const comparatorOfferItemActuelleRef =
    useTemplateRef<ComparatorOfferItemInstance | null>(
        "comparatorOfferItemActuelleRef"
    )
const comparatorOfferItemCompareeRef = useTemplateRef<
    ComparatorOfferItemInstance[] | null
>("comparatorOfferItemCompareeRef")

const errorForm = ref(false)
const timeout = ref<NodeJS.Timeout | null>(null)

/**
 * Quand l'utilisateur clique sur "Lancer la comparaison"
 */
async function onClickLaunchComparison(): Promise<void> {
    errorForm.value = false
    if (timeout.value) {
        clearTimeout(timeout.value)
    }
    comparatorOfferItemActuelleRef.value?.validate()
    comparatorOfferItemCompareeRef.value?.forEach(
        (item: { validate: () => void }) => {
            item.validate()
        }
    )

    if (
        comparatorOfferItemActuelleRef.value?.isValid &&
        comparatorOfferItemCompareeRef.value?.every(
            (item: { isValid: boolean }) => item.isValid
        )
    ) {
        offreActuelle.value = transformOfferModelToOfferActuelle(
            modelOffreActuelle.value
        )
        offresComparees.value = modelOffreComparees.value.map((offre, index) =>
            transformOfferModelToOfferAlternative(offre, index)
        )

        await comparatorStore.createCompareOffers()

        if (comparatorStore.hasErrorCompareOffers) {
            console.error(comparatorStore.errorCompareOffers)
        } else {
            editing.value = false
            // Mettre à jour les valeurs initiales après une comparaison réussie
            initialModelOffreActuelle.value = structuredClone(
                modelOffreActuelle.value
            )
            initialModelOffreComparees.value = structuredClone(
                modelOffreComparees.value
            )
        }
    } else {
        errorForm.value = true
    }
    timeout.value = setTimeout(() => {
        errorForm.value = false
    }, 3000)
}

/**
 * Transforme un modèle d'offre en une offre actuelle pour le comparateur
 * @param offre - Le modèle d'offre à transformer
 * @return L'offre actuelle transformée
 */
function transformOfferModelToOfferActuelle(
    offre: FormOfferModel
): ComparatorInputOffreActuelle {
    const period = offre.periods[0]
    return {
        idoffre: "offre_actuelle",
        nom_offre: offre.name,
        _id_supplier: offre.supplier,
        idperiode: moment().format("YYYY"),
        pricer: computedInputPricerOffer(period, offre.offer),
    }
}

/**
 * Transforme un modèle d'offre en une offre alternative pour le comparateur
 * @param offre - Le modèle d'offre à transformer
 * @param index - L'index de l'offre alternative
 */
function transformOfferModelToOfferAlternative(
    offre: FormOfferModel,
    index: number
): ComparatorInputOffreComparee {
    return {
        idoffre: `offre_alternative_${index}`,
        nom_offre: offre.name,
        _id_supplier: offre.supplier,
        periodes: offre.periods.map((period) => {
            return {
                idperiode: period.idperiode,
                pricer: computedInputPricerOffer(period, offre.offer),
            }
        }),
    }
}

/**
 * Calcule l'objet ComparatorInputPricerOffer en fonction de la période et de l'option tarifaire
 * @param period - La période contenant les prix
 * @param optionTarifaire - L'option tarifaire à appliquer
 */
function computedInputPricerOffer(
    period: PeriodItem,
    optionTarifaire: OptionTarifaire
): ComparatorInputPricerOffer {
    // Prix de l'obligation de capacité (€/kW/an) à inclure dans tous les cas.
    // Si la période n'a pas de prix de capacité obligatoire, on utilise la valeur par défaut.
    const prixHTTparkWparan_elecsoutirage_oc =
        period.capacite_obligatoire ?? DEFAULT_PRICER_CAPACITY_VALUE
    let oc = {
        prixHTTparkWparan_elecsoutirage_oc: prixHTTparkWparan_elecsoutirage_oc,
    } as ComparatorInputPricerOffer["structure_tarifaire"]["offre_tarifaire"]["oc"]

    if (period.capacity_method === CAPACITY_METHOD_ENERGIE_AVEC_COEFF) {
        oc = {
            ...oc,
            coefficient_capacite_plage: computedCoefficientCapacite(
                period,
                optionTarifaire
            ),
        }
    } else if (period.capacity_method === CAPACITY_METHOD_ENERGIE_SANS_COEFF) {
        oc = {
            ...oc,
            coefficient_capacite_plage:
                getCoefficientCapaciteUn(optionTarifaire),
        }
    }
    return {
        structure_tarifaire: {
            offre_tarifaire: {
                option_tarifaire: optionTarifaire,
                abonnement: {
                    prixHTTparmois_elecsoutirage_abonnement: period.abonnement,
                },
                consommation: {
                    prixHTTparkWh_elecsoutirage_plage:
                        computedConsommationPrice(period, optionTarifaire),
                },
                cee: {
                    prixHTTparkWh_elecsoutirage_cee: (period.cee ?? 0) / 1000,
                },
                oc,
                turpe: {
                    inclus_consommation: period.turpe,
                },
            },
        },
    }
}

/**
 * Calcule l'objet PrixHTTParKwgElecsoutiragePlage en fonction de la période et de l'option tarifaire
 * @param period - La période contenant les prix
 * @param optionTarifaire - L'option tarifaire à appliquer
 */
function computedConsommationPrice(
    period: PeriodItem,
    optionTarifaire: OptionTarifaire
): PrixHTTParKwgElecsoutiragePlage {
    switch (optionTarifaire) {
        case OptionTarifaire.Base:
            return {
                Base: (period.prix_base ?? 0) / 1000,
            }
        case OptionTarifaire.HPHC:
            return {
                HP: (period.prix_hp ?? 0) / 1000,
                HC: (period.prix_hc ?? 0) / 1000,
            }
        case OptionTarifaire.HS:
            return {
                HPH: (period.prix_hph ?? 0) / 1000,
                HCH: (period.prix_hch ?? 0) / 1000,
                HPB: (period.prix_hpb ?? 0) / 1000,
                HCB: (period.prix_hcb ?? 0) / 1000,
            }
        case OptionTarifaire.HS_PTE_Fixe:
            return {
                HPH: (period.prix_hph ?? 0) / 1000,
                HCH: (period.prix_hch ?? 0) / 1000,
                HPB: (period.prix_hpb ?? 0) / 1000,
                HCB: (period.prix_hcb ?? 0) / 1000,
                PTE: (period.prix_pte ?? 0) / 1000,
            }
    }
}

/**
 * Retourne un CoefficientCapacitePlage avec toutes les valeurs à 1 (méthode "Par énergie sans coefficient de capacité").
 */
function getCoefficientCapaciteUn(
    optionTarifaire: OptionTarifaire
): CoefficientCapacitePlage {
    switch (optionTarifaire) {
        case OptionTarifaire.Base:
            return { Base: 1 }
        case OptionTarifaire.HPHC:
            return { HP: 1, HC: 1 }
        case OptionTarifaire.HS:
            return { HPH: 1, HCH: 1, HPB: 1, HCB: 1 }
        case OptionTarifaire.HS_PTE_Fixe:
            return { HPH: 1, HCH: 1, HPB: 1, HCB: 1, PTE: 1 }
    }
}

function computedCoefficientCapacite(
    period: PeriodItem,
    optionTarifaire: OptionTarifaire
): CoefficientCapacitePlage {
    switch (optionTarifaire) {
        case OptionTarifaire.Base:
            return {
                Base: period.coefficient_capacite_base ?? 0,
            }
        case OptionTarifaire.HPHC:
            return {
                HP: period.coefficient_capacite_hp ?? 0,
                HC: period.coefficient_capacite_hc ?? 0,
            }
        case OptionTarifaire.HS:
            return {
                HPH: period.coefficient_capacite_hph ?? 0,
                HCH: period.coefficient_capacite_hch ?? 0,
                HPB: period.coefficient_capacite_hpb ?? 0,
                HCB: period.coefficient_capacite_hcb ?? 0,
            }
        case OptionTarifaire.HS_PTE_Fixe:
            return {
                HPH: period.coefficient_capacite_hph ?? 0,
                HCH: period.coefficient_capacite_hch ?? 0,
                HPB: period.coefficient_capacite_hpb ?? 0,
                HCB: period.coefficient_capacite_hcb ?? 0,
                PTE: period.coefficient_capacite_pte ?? 0,
            }
    }
}

const editing = ref(true)

// Stockage des valeurs initiales pour détecter les modifications
const initialModelOffreActuelle = ref<FormOfferModel | null>(null)
const initialModelOffreComparees = ref<FormOfferModel[] | null>(null)

function onClickEdit(): void {
    // Sauvegarder les valeurs initiales avant d'entrer en mode édition
    initialModelOffreActuelle.value = JSON.parse(
        JSON.stringify(modelOffreActuelle.value)
    )
    initialModelOffreComparees.value = JSON.parse(
        JSON.stringify(modelOffreComparees.value)
    )
    editing.value = true
}

function onClickCancelEdit(): void {
    // Restaurer les valeurs initiales
    if (initialModelOffreActuelle.value) {
        modelOffreActuelle.value = JSON.parse(
            JSON.stringify(initialModelOffreActuelle.value)
        )
    }
    if (initialModelOffreComparees.value) {
        modelOffreComparees.value = JSON.parse(
            JSON.stringify(initialModelOffreComparees.value)
        )
    }
    editing.value = false
    errorForm.value = false
}

function onClickNewComparison(): void {
    // Réinitialiser toutes les données pour une nouvelle comparaison
    modelOffreActuelle.value = {
        supplier: "",
        offer: OptionTarifaire.Base,
        name: "",
        periods: [],
    }

    modelOffreComparees.value = [
        {
            supplier: "",
            offer: OptionTarifaire.Base,
            name: "",
            periods: [],
            indexTransition: 0,
        },
    ]

    indexTransition.value = 0

    // Remettre à zéro l'analyse actuelle
    comparatorStore.currentAnalysis = null

    // Réinitialiser les valeurs initiales
    initialModelOffreActuelle.value = null
    initialModelOffreComparees.value = null

    // Activer le mode édition
    editing.value = true

    // Réinitialiser les erreurs
    errorForm.value = false
}
</script>
<template>
    <LoadingApp v-if="isFetchingInitialData" class="page"></LoadingApp>
    <div v-else class="page page-building-comparateur-offres">
        <HeaderPageBuilding
            :title="t('title')"
            class="border-b border-surface-300 pb-8"
        ></HeaderPageBuilding>
        <!-- Paramètrages-->
        <div
            class="flex flex-col gap-8 pb-8"
            :class="{
                'border-b border-surface-300': !!currentAnalysis && !editing,
            }"
        >
            <div class="flex justify-between items-center gap-2">
                <h2 class="text-xl font-bold text-primary-700">
                    {{ t("parameters") }}
                </h2>
                <!-- Boutons quand pas en édition -->
                <div v-if="!editing" class="flex gap-2 items-center">
                    <BaseButton
                        @click="onClickEdit"
                        :label="t('edit')"
                        icon="pi pi-pencil"
                        icon-pos="left"
                    />

                    <!-- Trait vertical séparateur -->
                    <div
                        v-if="currentAnalysis"
                        class="h-12 w-0.5 bg-surface-300"
                    ></div>

                    <!-- Bouton Nouvelle comparaison - visible dès qu'il y a une analyse existante -->
                    <BaseButton
                        v-if="currentAnalysis"
                        @click="onClickNewComparison"
                        :label="t('new_comparison')"
                        icon="pi pi-plus"
                        icon-pos="left"
                    />
                </div>
                <!-- Boutons d'édition quand en mode édition et qu'il y a déjà une analyse -->
                <div v-else-if="editing && currentAnalysis" class="flex gap-2">
                    <BaseButton
                        @click="onClickCancelEdit"
                        :label="t('cancel_edit')"
                        variant="outlined"
                        icon="pi pi-times"
                        icon-pos="left"
                    />
                    <BaseButton
                        @click="onClickLaunchComparison"
                        :label="t('validate_modifications')"
                        :loading="isFetchingCompareOffers"
                        icon="pi pi-check"
                        icon-pos="right"
                    />
                </div>
            </div>
            <!-- Paramètres -->
            <Transition name="slide-down">
                <div v-if="editing" class="flex flex-col gap-8 overflow-hidden">
                    <ComparatorMeterChoiceCard key="card-meter-choice" />

                    <div
                        class="flex flex-col gap-4"
                        v-if="getSourceById?.eligible"
                    >
                        <!-- Offre actuelle (de référence) -->
                        <ComparatorOfferItem
                            :actual="true"
                            v-model:offer="modelOffreActuelle.offer"
                            v-model:supplier="modelOffreActuelle.supplier"
                            v-model:name="modelOffreActuelle.name"
                            v-model:periods="modelOffreActuelle.periods"
                            v-model:energieparposte-elec="
                                consommationByPlageFournisseur
                            "
                            v-model:use-energieparposte-elec="
                                useEnergieparposteElec
                            "
                            ref="comparatorOfferItemActuelleRef"
                        />
                        <TransitionGroup
                            name="list"
                            tag="ul"
                            class="flex flex-col gap-6"
                            appear
                        >
                            <li
                                v-for="(
                                    offreComparee, index
                                ) in modelOffreComparees"
                                :key="index"
                            >
                                <ComparatorOfferItem
                                    :key="`offre-comparee-${offreComparee.indexTransition}`"
                                    :actual="false"
                                    :index="index"
                                    v-model:offer="offreComparee.offer"
                                    v-model:supplier="offreComparee.supplier"
                                    v-model:name="offreComparee.name"
                                    v-model:periods="offreComparee.periods"
                                    :energieparposte-elec="
                                        consommationByPlageFournisseur
                                    "
                                    @delete:offer="onClickDeleteOffer(index)"
                                    ref="comparatorOfferItemCompareeRef"
                                />
                            </li>
                        </TransitionGroup>
                        <BaseButton
                            class="w-fit"
                            :label="t('add_offer')"
                            icon="pi pi-plus"
                            icon-pos="left"
                            text
                            @click="onClickAddOffer"
                        />
                    </div>
                    <!-- Lancer la comparaison -->
                    <BaseMessage v-if="errorForm" severity="error" class="mx-2">
                        {{ t("error_compare_offers") }}
                    </BaseMessage>
                    <BaseMessage
                        v-if="hasErrorCompareOffers"
                        severity="error"
                        class="mx-2"
                    >
                        {{
                            te(`code_error.${errorCompareOffers?.code}`)
                                ? t(`code_error.${errorCompareOffers?.code}`)
                                : t(`code_error.general`)
                        }}
                    </BaseMessage>

                    <!-- Boutons supplémentaires en bas quand en mode édition avec analyse existante -->
                    <div
                        v-if="getSourceById?.eligible"
                        class="flex gap-4 justify-center pt-4 border-t border-surface-200"
                    >
                        <BaseButton
                            v-if="currentAnalysis"
                            @click="onClickCancelEdit"
                            :label="t('cancel_edit')"
                            variant="outlined"
                            icon="pi pi-times"
                            icon-pos="left"
                            class="min-w-[12rem]"
                        />
                        <BaseButton
                            @click="onClickLaunchComparison"
                            :label="t('validate_modifications')"
                            :loading="isFetchingCompareOffers"
                            icon="pi pi-check"
                            icon-pos="right"
                            class="min-w-[12rem]"
                        />
                    </div>
                </div>
            </Transition>
        </div>
        <!-- Résultat de la comparaison -->
        <ComparatorResultCard
            v-if="currentAnalysis && !editing"
            name="Comparaison des offres"
            :created-at="new Date()"
            :output="currentAnalysis"
        />
        <!-- Résultat des analyses sauvegardées -->
        <ComparatorHistoryTable />
    </div>
</template>
<style scoped>
/* ================================ */
/* ANIMATIONS DE TRANSITION */
/* ================================ */
.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.hidden {
    max-height: 0;
    opacity: 0;
}

/* Slide down animation */
.slide-down-enter-active {
    transition:
        max-height 0.3s ease-out,
        opacity 0.3s ease-out;
}

.slide-down-leave-active {
    transition:
        max-height 0.3s ease-in,
        opacity 0.3s ease-in;
}

.slide-down-enter-from {
    opacity: 0;
    max-height: 0;
}

.slide-down-leave-to {
    opacity: 0;
    max-height: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
    opacity: 1;
    max-height: 1000px;
}
</style>
